/**
 * DealRoomStatusLine — operational status sentence for the Deal Room.
 *
 * Derives a single calm sentence from data already on the deal object,
 * answering "what's the state of this deal *right now*?". No new
 * components, no extra tabs, no chrome — sits inside the Flow tab.
 *
 * The line follows a strict template:
 *   {primary_state} · {time_pressure} · {social_context}
 *
 * Example:
 *   "Awaiting TA6 from seller solicitor · 5 days outstanding ·
 *    Last message 11 min ago"
 *
 * Falls back gracefully when sources are sparse — never renders a
 * partial sentence with dangling separators.
 */
import React, { useMemo } from "react";
import { LIGHT } from "./RoomFlow";

const _ts = (v) => (typeof v === "number" ? v : v ? Date.parse(v) : 0);

const _relativeMins = (ms) => {
  const diff = Math.max(0, Date.now() - ms);
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const _daysOutstanding = (ms) => {
  if (!ms) return null;
  const diff = Math.max(0, Date.now() - ms);
  const days = Math.floor(diff / 86_400_000);
  return days;
};

const _latestActiveRisk = (deal) => {
  // Read the most recent `risk_flagged` activity that has NOT been
  // subsequently cleared. Server-side derivations populate these
  // continuously; the status line surfaces the headline of the freshest
  // unresolved one, which is the most actionable signal we have.
  //
  // The frontend chainStore maps `activity_type → kind`, so a row from a
  // `risk_flagged` activity will arrive with `kind: "risk"`. Cleared rows
  // arrive with `activity_type === "risk_cleared"` AND a `cleared_for_activity_id`
  // in their meta. We also stamp `meta.cleared_at` on the original row
  // when it's been cleared, which we use as the authoritative "resolved"
  // signal.
  const acts = deal?.activity || [];
  // Track which activity ids have already been cleared so we don't surface them.
  const clearedIds = new Set();
  for (const a of acts) {
    const meta = a.meta || {};
    if (a.activity_type === "risk_cleared" && meta.cleared_for_activity_id) {
      clearedIds.add(meta.cleared_for_activity_id);
    }
  }
  for (const a of acts) {
    const meta = a.meta || {};
    const isRiskRow = a.activity_type === "risk_flagged" || a.kind === "risk";
    if (!isRiskRow) continue;
    // Cleared rows ALSO have kind === "risk" (frontend kindMap collapses
    // risk_flagged + risk_cleared → "risk"). Identify clearings by their
    // own meta marker and skip them.
    if (meta.cleared_for_activity_id) continue;
    if (meta.cleared_at) continue;
    if (a.id && clearedIds.has(a.id)) continue;
    // Only surface server-derived risks (operational_derivation source).
    // Manual `risk_flagged` rows still appear in the Flow, but we don't
    // promote them to the status line — they already have their own card.
    if (meta.source && meta.source !== "operational_derivation") continue;
    return {
      title: a.title || a.text,
      severity: meta.severity || "medium",
      derivation_kind: meta.derivation_kind || null,
    };
  }
  return null;
};


const _topPendingDoc = (deal, docRequests = []) => {
  // Prefer an explicit doc-request row; fall back to body-mined activity
  if (docRequests && docRequests.length > 0) {
    const open = docRequests.filter((r) => r.status === "pending" || r.status === "awaiting" || r.status === "requested");
    if (open.length > 0) {
      const sorted = [...open].sort((a, b) => _ts(a.created_at || a.at) - _ts(b.created_at || b.at));
      const top = sorted[0];
      return {
        name: top.name || top.document_name || top.body || "Document",
        from: top.from_party || top.from_role || top.target_role || null,
        since: _ts(top.created_at || top.at),
      };
    }
  }
  return null;
};

const _milestonesRemaining = (deal) => {
  // The deal carries a stage; if we have flow-intelligence checklist
  // counts elsewhere, prefer those. Otherwise infer a conservative
  // "X to {next named stage}" sentence using the canonical stage order.
  const ORDER = [
    "instruction", "marketing", "offer_accepted", "memo",
    "searches_ordered", "enquiries", "mortgage_offer", "exchange_ready",
    "exchanged", "completion",
  ];
  const cur = (deal?.stage || "").toLowerCase();
  const idx = ORDER.indexOf(cur);
  if (idx < 0) return null;
  const nextNamed = ORDER.slice(idx + 1).find((s) => ["mortgage_offer", "exchanged", "completion"].includes(s));
  if (!nextNamed) return null;
  const distance = ORDER.indexOf(nextNamed) - idx;
  if (distance <= 0) return null;
  const label = { mortgage_offer: "mortgage offer", exchanged: "exchange", completion: "completion" }[nextNamed];
  return { distance, label };
};

const _parseDate = (v) => {
  if (!v) return null;
  if (typeof v === "number") return new Date(v);
  if (v instanceof Date) return v;
  const t = Date.parse(String(v));
  return isNaN(t) ? null : new Date(t);
};

const _fmtShortDate = (d) =>
  d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const _daysUntil = (d) => Math.ceil((d.getTime() - Date.now()) / 86_400_000);

const _targetSegment = (deal) => {
  // Surface near-term target dates with the strongest tone when imminent.
  // Precedence: exchange (closer to money) > completion. Both within 14
  // days are shown; either > 14 days OR in the past is suppressed.
  const targets = [
    { label: "exchange",   field: deal?.targetExchange   || deal?.target_exchange },
    { label: "completion", field: deal?.targetCompletion || deal?.target_completion },
  ];
  for (const t of targets) {
    const date = _parseDate(t.field);
    if (!date) continue;
    const days = _daysUntil(date);
    if (days < 0) continue;          // past dates are not operational signal
    if (days > 14) continue;         // > 2 weeks out is just calendar noise
    return {
      label: `Targeting ${t.label} ${_fmtShortDate(date)}`,
      pressure: days === 0
        ? "today"
        : `${days} day${days === 1 ? "" : "s"} out`,
      tone: days <= 3 ? "warning" : (days <= 7 ? "warning" : "default"),
    };
  }
  return null;
};

export const DealRoomStatusLine = ({ deal, docRequests = [] }) => {
  const status = useMemo(() => {
    if (!deal) return null;

    const segments = [];

    // 1) Primary state — prefer the server-side derived risk (most
    //    specific, names the bottleneck precisely). Falls back to the
    //    client-side doc-request heuristic, then to blocked/risk flags,
    //    then to the generic stage label.
    const activeRisk = _latestActiveRisk(deal);
    const pending = _topPendingDoc(deal, docRequests);
    if (activeRisk) {
      segments.push({
        key: "state",
        label: activeRisk.title,
        tone: activeRisk.severity === "high" ? "danger" : "warning",
      });
    } else if (pending) {
      const fromBit = pending.from ? ` from ${pending.from}` : "";
      segments.push({ key: "state", label: `Awaiting ${pending.name}${fromBit}`, tone: "warning" });
      const days = _daysOutstanding(pending.since);
      if (days != null && days > 0) {
        segments.push({ key: "pressure", label: `${days} day${days === 1 ? "" : "s"} outstanding`, tone: days >= 7 ? "danger" : "muted" });
      }
    } else if (deal.status === "Blocked") {
      segments.push({ key: "state", label: "Deal blocked", tone: "danger" });
    } else if (deal.risk === "high") {
      segments.push({ key: "state", label: "Risk flagged on this deal", tone: "danger" });
    } else {
      const stagePretty = (deal.stage || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      segments.push({ key: "state", label: `Progressing through ${stagePretty || "the chain"}`, tone: "default" });
    }

    // 2) Imminent target dates — exchange / completion within 14 days
    //    surface as a calm operational headline. Past or > 2 weeks out
    //    are suppressed so the line stays signal-only.
    const target = _targetSegment(deal);
    if (target) {
      segments.push({ key: "target", label: target.label, tone: target.tone });
      segments.push({ key: "target_pressure", label: target.pressure, tone: target.tone });
    }

    // 3) Social context — when did anyone last move this deal?
    const lastAct = Math.max(
      ...(deal.activity || []).map((a) => _ts(a.at)),
      ...(deal.messages || []).map((m) => _ts(m.at)),
      0,
    );
    if (lastAct > 0) {
      segments.push({ key: "social", label: `Last activity ${_relativeMins(lastAct)}`, tone: "muted" });
    }

    // 4) Milestones remaining — keeps the agent oriented forward.
    const miles = _milestonesRemaining(deal);
    if (miles) {
      segments.push({
        key: "milestones",
        label: `${miles.distance} milestone${miles.distance === 1 ? "" : "s"} to ${miles.label}`,
        tone: "default",
      });
    }

    return segments.length > 1 ? segments : null;
  }, [deal, docRequests]);

  if (!status) return null;

  const toneColor = (t) => {
    switch (t) {
      case "danger": return "#B91C1C";
      case "warning": return "#92400E";
      case "muted": return LIGHT.textMuted;
      default: return LIGHT.textPrimary;
    }
  };

  return (
    <div
      data-testid="dealroom-operational-status"
      className="mb-5 px-4 py-3 rounded-xl flex flex-wrap items-center gap-x-2 gap-y-1"
      style={{
        background: "linear-gradient(120deg, #F8FAFC 0%, #FFFFFF 100%)",
        border: `1px solid ${LIGHT.border}`,
      }}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: "#10B981", boxShadow: "0 0 0 3px rgba(16,185,129,0.18)" }}
        aria-hidden
      />
      {status.map((seg, i) => (
        <React.Fragment key={seg.key}>
          {i > 0 && <span className="text-[12px]" style={{ color: LIGHT.textSubtle }}>·</span>}
          <span
            data-testid={`dealroom-operational-status-${seg.key}`}
            className="text-[12.5px] font-semibold"
            style={{ color: toneColor(seg.tone) }}
          >
            {seg.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

export default DealRoomStatusLine;
